import { Button, List, ListItem, ListItemText } from '@material-ui/core';
import React from 'react';
import { RouteLink } from '../../components/Shared/RouteLink';
import { withLocale } from '../../hocs/withLocale';
import routes from '../../library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import moment from 'moment';

interface Teacher {
  name: string;
  [key: string]: any;
}

interface Room {
  name: string;
  color: string;
  [key: string]: any;
}

interface EventObject {
  duration: number;
  limit?: number;
  allowSelfSignUp?: boolean;
  [key: string]: any;
}

interface ClassInfo extends EventObject {
  groupId: string;
  [key: string]: any;
}

interface ClassEvent {
  start: string;
  title: string;
  classInfo?: ClassInfo;
  event?: EventObject;
  registeredCount: number;
  note?: string;
  instructorName?: string;
  teachers?: Teacher[];
  rooms: Room[];
  [key: string]: any;
}

interface Studio {
  [key: string]: any;
}

interface Owner {
  timezone?: string;
  [key: string]: any;
}

interface SingleClassViewProps {
  classEvent: ClassEvent;
  studio: Studio;
  owner?: Owner;
  translate: (key: string, data?: any) => string;
}

export const SingleClassView = withLocale(({ classEvent, studio, translate, owner }: SingleClassViewProps) => {
  const { classInfo, event } = classEvent;
  const eventObject = classInfo || event;

  if (!eventObject) {
    return null;
  }

  let start = moment.utc(classEvent.start);
  let end = moment.utc(start).add(eventObject.duration, 'minute').startOf('minute');
  let limit = eventObject ? eventObject.limit : null;
  let sportsLeft: number | undefined;
  let registrationExpired = false;

  if (classInfo) {
    const isClassBookable = LayoutHelper.isClassBookable(
      owner && owner.timezone ? owner.timezone : moment.tz.guess(),
      studio,
      start
    );
    if (!isClassBookable) {
      registrationExpired = true;
    }
    if (limit) {
      sportsLeft = limit - classEvent.registeredCount;
      if (sportsLeft < 1) {
        registrationExpired = true;
      }
    }
  } else {
    registrationExpired = true;
  }

  let renderLimit = () => {
    if (registrationExpired) {
      return null;
    }
    let spotsInfo;
    if (classInfo) {
      if (limit) {
        if (sportsLeft && sportsLeft > 0) {
          spotsInfo = `😀${sportsLeft}/${limit}`;
        } else {
          spotsInfo = `😡${translate('studio.classBookedOut')}`;
        }
      }
    } else {
      if (limit) {
        spotsInfo = `Max. ${limit}`;
      }
    }
    return (
      <ListItem>
        <ListItemText primary={`${classEvent.note || ''} ${classEvent.instructorName || ''}`} secondary={spotsInfo} />
      </ListItem>
    );
  };

  return (
    <List>
      <ListItem>
        <ListItemText
          primary={classEvent.title}
          secondary={`${start.format('DD MMM YYYY HH:mm')} - ${end.format('HH:mm')}`}
        />
      </ListItem>
      {eventObject.allowSelfSignUp && !registrationExpired && classInfo && (
        <ListItem>
          <Button
            component={RouteLink}
            target="_blank"
            fullWidth
            variant="outlined"
            to={routes.getClassLink(studio, classInfo.groupId)}
            color="secondary"
          >
            {translate('studio.classSignUp')}
          </Button>
        </ListItem>
      )}
      {classEvent.teachers && classEvent.teachers.length > 0 && (
        <ListItem>
          {classEvent.teachers.map((teacher, index) => {
            return <ListItemText key={index} primary={teacher.name} />;
          })}
        </ListItem>
      )}
      {classEvent.rooms.map((room, index) => {
        if (!room) {
          return null;
        }
        return (
          <ListItem key={index}>
            <ListItemText
              primary={<span style={{ borderLeft: `thick solid ${room.color}` }}>&nbsp;{room.name}</span>}
            />
          </ListItem>
        );
      })}
      {renderLimit()}
    </List>
  );
});
